import Drawers from '@common/ui/drawes/Drawers';
import Metatags from '@components/common/Metatags';
import Breadcrumbs from '@components/listing/Breadcrumb';
import { ProductHero, ProductLook, ProductRecommendation, ProductSelection } from '@moncler/components/product';
import VtoModal from '@moncler/components/vto/VtoModal';
import { ProductActionTypes } from '@state/features/common.types';
import analytics from '@utils/analytics';
import JsonLd from '@utils/JsonLd';
import { getSizeAvailability } from '@utils/product';
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ProductDetailsTab from './ProductDetailsTab';
import api from '@common/utils/api';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import customEventBus from '@common/scripts/libs/eventBus';
import ProductDiscoverMore from '@moncler/components/product/ProductDiscoverMore';
import ProductGallery from '@moncler/components/product/ProductGallery';
import ProductImageAnimated from '@moncler/components/product/ProductImageAnimated';
import useBreakpoint from '@common/utils/useBreakpoint';
import ProductShippingAndReturnsTabs from '@moncler/components/product/ProductShippingAndReturnsTabs';
import { throttle } from '@common/utils/functions';
import ScrollHandler from './ScrollHandler';
import GiftCardBalanceModal from './GiftCardBalanceModal';
import classNames from 'classnames';
import useProductEditorialContents from '@moncler/hooks/useProductEditorialContents';
import useQueryStringBoolean from '@common/utils/useQueryStringBoolean';
const ProductPage = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { t } = useTranslation();
    let { pid } = useParams();
    const dispatch = useDispatch();
    const current = useSelector((state) => state.product).current;
    const redirect = useSelector((state) => state.redirect);
    const [galleryIsOpen, setGalleryIsOpen] = useState(false);
    const [previousPid, setpreviousPid] = useState('');
    const [previousPid2, setpreviousPid2] = useState('');
    const [heroImageLoaded, setHeroImageLoaded] = useState(window.ssr ? true : false);
    const [media, setMedia] = useState([]);
    const [sizeguideOpen, setSizeguideOpen] = useState(false);
    const [openVTO, setOpenVTO] = useState(location.search.includes('openVTO'));
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [shippingOpen, setShippingOpen] = useState(false);
    const [shippingAndReturns, setShippingAndReturns] = useState(null);
    const [shippingAndReturnsCheck, setShippingAndReturnsCheck] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMounted, toggle] = useReducer((p) => !p, true);
    const [checkBalanceOpen, setCheckBalanceOpen] = useQueryStringBoolean('openCBModal');
    const currentBreakpoint = useBreakpoint();
    useEffect(() => {
        const handleScroll = throttle(() => {
            if (window.scrollY > 0) {
                setIsScrolled(true);
            }
            else {
                setIsScrolled(false);
            }
        }, 100);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    // sticky behavior
    useEffect(() => {
        let lastScrollTop = 0;
        let lastpos = 0;
        const offsetScroll = 32;
        let scrollAmout = 0;
        let header = document.querySelector('header');
        let firstTime = true;
        if (currentBreakpoint < window.mqObj.md) {
            const onScroll = () => {
                if (firstTime) {
                    firstTime = false;
                    return;
                }
                scrollAmout = header && header.offsetHeight;
                const translateAmount = scrollAmout + 16;
                let st = window.pageYOffset || document.documentElement.scrollTop;
                if (st > lastScrollTop) {
                    if (st >= scrollAmout && st - lastpos >= offsetScroll) {
                        lastpos = st <= 0 ? 0 : st;
                        if (header)
                            header.style.transform = `translateY(-${translateAmount}px)`;
                    }
                    // downscroll code
                }
                else {
                    // upscroll code
                    if (lastpos - st >= offsetScroll) {
                        lastpos = st <= 0 ? 0 : st;
                        header === null || header === void 0 ? void 0 : header.style.removeProperty('transform');
                    }
                }
                lastScrollTop = st <= 0 ? 0 : st;
            };
            window.addEventListener('scroll', onScroll);
            return () => {
                header === null || header === void 0 ? void 0 : header.style.removeProperty('transform');
                window.removeEventListener('scroll', onScroll);
            };
        }
    }, [currentBreakpoint]);
    const sendAnalyticsEvents = () => {
        // composing subCategory as per OE-5541
        let pageDesignValue = window.datalayerdata.pageDesign;
        if (current.editorial && current.editorial.pageDesignerContent) {
            pageDesignValue = 'pdp-editorial';
        }
        window.datalayerdata = Object.assign(Object.assign({}, window.datalayerdata), { pageCategory: 'pdp', subCategory: current.id, pageDesign: pageDesignValue ? pageDesignValue : 'pdp-standard' });
        analytics.sendGeneralVariables();
    };
    useEffect(() => {
        if (redirect.error && redirect.redirectOnError) {
            window.location.href = redirect.redirectOnError;
        }
    }, [redirect]);
    // deactivate Sprinklr chat on modal opening
    useEffect(() => {
        if ((typeof galleryIsOpen == 'boolean' && galleryIsOpen) || (typeof galleryIsOpen == 'number' && galleryIsOpen >= 0)) {
            window.sprChat && window.sprChat('disable');
        }
        else {
            window.sprChat && window.sprChat('enable');
        }
    }, [galleryIsOpen]);
    const firstShow = useRef(true);
    useEffect(() => {
        // solo quando entro in PDP da una PLP (quindi dal routing client side)
        if (firstShow.current && current) {
            sendAnalyticsEvents();
            analytics.productDetailViewsWithTransitions(current);
            customEventBus.emit('PDP:loaded', {});
            let header = document.querySelector('header');
            header === null || header === void 0 ? void 0 : header.classList.add('header--pdp');
            if (current.imageBackground === 'white') {
                header === null || header === void 0 ? void 0 : header.classList.add('white-bg');
            }
        }
        firstShow.current = false;
        return () => {
            firstShow.current = true;
        };
    }, []);
    const refreshPDP = (pid, sendAnalytics) => {
        ScrollTrigger.disable(true, true);
        dispatch({
            type: ProductActionTypes.FETCH_PRODUCT,
            payload: {
                pid,
                sendAnalytics,
            },
        });
    };
    // handle load or refresh of the PDP
    useEffect(() => {
        document.documentElement.style.setProperty('--sm-first-vh', window.innerHeight + 'px');
        if (current && pid) {
            if (previousPid.includes(current.masterId) && pid.includes(current.masterId)) {
                // Color change inside PDP
                refreshPDP(pid);
            }
            else if (current.id != pid) {
                // al back del browser
                // PDP <-> PDP
                refreshPDP(pid, 'withTransitions');
            }
        }
        else if (pid) {
            // Refresh/landing directily on PDP
            // console.info('no current - Refresh/landing directily on PDP');
            refreshPDP(pid, 'detail');
        }
        // change canonical url
        const canonical = document.querySelector('link[rel="canonical"]');
        canonical && canonical.setAttribute('href', location.href);
        setpreviousPid(pid);
    }, [pid]);
    const getSizeSchema = (variant, selectedColor) => {
        var _a, _b, _c, _d;
        return {
            '@type': 'Product',
            name: `${current === null || current === void 0 ? void 0 : current.productName} ${t('label.size')} : ${variant.displayValue}`,
            image: (_a = current.imgs) === null || _a === void 0 ? void 0 : _a.urls,
            description: `${current === null || current === void 0 ? void 0 : current.shortDescription} ${t('label.size')} : ${variant.displayValue}`,
            color: selectedColor,
            size: variant.displayValue,
            offers: {
                '@type': 'Offer',
                url: `${location.origin}${location.pathname}?size=${variant.displayValue}`,
                priceCurrency: (_c = (_b = current === null || current === void 0 ? void 0 : current.price) === null || _b === void 0 ? void 0 : _b.sales) === null || _c === void 0 ? void 0 : _c.currency,
                price: variant.price,
                itemCondition: 'https://schema.org/NewCondition',
                availability: getSizeAvailability(variant, (_d = current.availability) === null || _d === void 0 ? void 0 : _d.preorder),
            },
        };
    };
    const getVariantsSchema = (variationAttributes) => {
        const variantsSchema = [];
        let colors = [];
        let selectedColor;
        variationAttributes.forEach((va) => {
            if (va.attributeId === 'color') {
                // Those last
                selectedColor = va.displayValue;
                va.values.forEach((color) => {
                    var _a;
                    // We only need the sizes of the other colors
                    if (color.value !== va.value) {
                        (_a = color.sizes) === null || _a === void 0 ? void 0 : _a.forEach((size) => {
                            const url = location.origin + color.productUrl + `?size=${size}`;
                            colors.push({ url: url });
                        });
                    }
                });
            }
            else if (va.attributeId === 'size') {
                // Those first
                va.values.forEach((size) => {
                    variantsSchema.push(getSizeSchema(size, selectedColor));
                });
            }
        });
        return variantsSchema.concat(colors);
    };
    const getProductGroupSchema = () => {
        var _a;
        return {
            '@context': 'https://schema.org',
            '@type': 'ProductGroup',
            '@id': current === null || current === void 0 ? void 0 : current.masterId,
            name: current === null || current === void 0 ? void 0 : current.productName,
            image: (_a = current.imgs) === null || _a === void 0 ? void 0 : _a.urls,
            description: current === null || current === void 0 ? void 0 : current.shortDescription,
            brand: {
                '@type': 'Brand',
                name: 'Moncler',
            },
            productGroupID: current === null || current === void 0 ? void 0 : current.masterId,
            variesBy: ['https://schema.org/size', 'https://schema.org/color'],
            hasVariant: getVariantsSchema(current.variationAttributes),
        };
    };
    const productSchemaData = useMemo(() => (current ? getProductGroupSchema() : undefined), [current]);
    const getBreadcrumbSchemaData = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: current.breadcrumbs.map((item, index) => ({
                '@type': 'ListItem',
                position: ++index,
                name: item.htmlValue,
                item: location.origin + item.url,
            })),
        };
    };
    const breadcrumbSchemaData = useMemo(() => { var _a; return (((_a = current === null || current === void 0 ? void 0 : current.breadcrumbs) === null || _a === void 0 ? void 0 : _a.length) > 0 ? getBreadcrumbSchemaData() : undefined); }, [current]);
    // handle image loaded to start animations
    const handleImageLoaded = () => {
        const image = document.querySelector('.product-container .product-image img');
        // console.info('immagine: ', image);
        if (!image) {
            setHeroImageLoaded(true);
            return;
        }
        image.addEventListener('load', (event) => {
            // let isLoaded = image.complete && image.naturalHeight !== 0;
            // console.log('image loaded');
            setHeroImageLoaded(true);
        });
    };
    const setPlayButtonVideo = () => {
        const videoPauseButton = document.querySelector('.product-video-pause');
        if (videoPauseButton) {
            videoPauseButton.setAttribute('aria-pressed', 'false');
        }
    };
    const firstLoad = useRef(true);
    useEffect(() => {
        if (firstLoad.current && current) {
            // masterId for live chat analytics event [OE-19829]
            window.datalayerdata = Object.assign(Object.assign({}, window.datalayerdata), { masterId: current.masterId });
            window.addEventListener('message', function (message) {
                if (message.data && message.data.event_type === 'spottImpression') {
                    analytics.hostsposts('hotspots view', current.masterId);
                }
                if (message.data && message.data.event_type === 'spottInteraction') {
                    analytics.hostsposts('hotspots hover', current.masterId);
                }
            });
            // setTimeout(() => {
            // handleImageLoaded();
            // }, 500);
            // fallback per image loaded
            setTimeout(() => {
                if (!heroImageLoaded) {
                    setHeroImageLoaded(true);
                }
            }, 3000);
            firstLoad.current = false;
        }
    }, [current]);
    // scroll top 0 nella navigazione tra PDP
    useEffect(() => {
        if (current && pid && previousPid2 != '' && previousPid2 != pid) {
            // from a PDP to another by clicking on a carousel / shop the look product
            sendAnalyticsEvents();
            analytics.productDetailViewsWithTransitions(current);
            shippingAndReturns && setShippingOpen(false);
            if (!firstShow.current) {
                window.scrollTo(0, 0);
            }
        }
        setpreviousPid2(pid);
    }, [current, pid]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        // Priorità alle nextImages se disponibili, altrimenti usa imgs.urls
        let sourceUrls = [];
        const hasOnlyOneImage = (((_c = (_b = (_a = current === null || current === void 0 ? void 0 : current.nextImages) === null || _a === void 0 ? void 0 : _a.next) === null || _b === void 0 ? void 0 : _b.urls) === null || _c === void 0 ? void 0 : _c.length) == 1 && ((_g = (_f = (_e = (_d = current === null || current === void 0 ? void 0 : current.nextImages) === null || _d === void 0 ? void 0 : _d.next) === null || _e === void 0 ? void 0 : _e.urls) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.includes('_F'))) ||
            (((_k = (_j = (_h = current === null || current === void 0 ? void 0 : current.nextImages) === null || _h === void 0 ? void 0 : _h['plp-second']) === null || _j === void 0 ? void 0 : _j.urls) === null || _k === void 0 ? void 0 : _k.length) == 1 && ((_p = (_o = (_m = (_l = current === null || current === void 0 ? void 0 : current.nextImages) === null || _l === void 0 ? void 0 : _l['plp-second']) === null || _m === void 0 ? void 0 : _m.urls) === null || _o === void 0 ? void 0 : _o[0]) === null || _p === void 0 ? void 0 : _p.includes('_F')));
        if (!hasOnlyOneImage && ((_s = (_r = (_q = current === null || current === void 0 ? void 0 : current.nextImages) === null || _q === void 0 ? void 0 : _q.next) === null || _r === void 0 ? void 0 : _r.urls) === null || _s === void 0 ? void 0 : _s.length) > 0) {
            sourceUrls = current.nextImages.next.urls;
        }
        else if (((_t = current === null || current === void 0 ? void 0 : current.imgs) === null || _t === void 0 ? void 0 : _t.urls) && ((_u = current === null || current === void 0 ? void 0 : current.imgs) === null || _u === void 0 ? void 0 : _u.urls.length) > 0) {
            sourceUrls = current.imgs.urls.filter((url) => !(url.includes('_F.') || url.includes('_F1.')));
        }
        if (sourceUrls.length > 0) {
            let newMedia = sourceUrls.map((el) => el);
            // exclude F and F1 duplicate of R on standard PDP
            newMedia = newMedia.filter((el) => !(el.includes('_F.') || el.includes('_F1.')));
            if (current === null || current === void 0 ? void 0 : current.videos) {
                for (const [key, value] of Object.entries(current === null || current === void 0 ? void 0 : current.videos)) {
                    newMedia.splice(parseInt(key), 0, value);
                }
            }
            // Filtro per le immagini MBM settate sulle custom preferences
            if ((current === null || current === void 0 ? void 0 : current.isMbm) && props.mbmImageConfiguration) {
                const mbmImageConfiguration = props.mbmImageConfiguration.split(',');
                const isMBMPreferencesImage = (value) => {
                    let mediaUrl;
                    mbmImageConfiguration.forEach((el) => {
                        if (value.includes('_' + el.trim())) {
                            mediaUrl = value;
                        }
                    });
                    return mediaUrl ? mediaUrl : false;
                };
                newMedia = newMedia.filter((el) => isMBMPreferencesImage(el));
            }
            // aggiunngo, se presenti, i media del MBM
            if (((_w = (_v = current.mbm) === null || _v === void 0 ? void 0 : _v.palettes) === null || _w === void 0 ? void 0 : _w.length) > 0) {
                newMedia = newMedia.concat(current.mbm.palettes.map((el) => { var _a; return `palette_${el.productVideo || ((_a = el === null || el === void 0 ? void 0 : el.productImage) === null || _a === void 0 ? void 0 : _a.path)}`; }));
            }
            const finalMedia = newMedia.map((el) => (el.startsWith('palette_') ? el.substring(8) : el));
            setMedia(finalMedia);
            handleImageLoaded();
            // exclude video at position 0
            // let productGallery = newMedia.filter((url) => !url.includes('_V2'));
        }
    }, [(_a = current === null || current === void 0 ? void 0 : current.imgs) === null || _a === void 0 ? void 0 : _a.urls, (_c = (_b = current === null || current === void 0 ? void 0 : current.nextImages) === null || _b === void 0 ? void 0 : _b.next) === null || _c === void 0 ? void 0 : _c.urls, (_d = current === null || current === void 0 ? void 0 : current.mbm) === null || _d === void 0 ? void 0 : _d.palettes]);
    useEffect(() => {
        var _a;
        // add class to footer to increase space only mobile pdp
        (_a = document.querySelector('.footer')) === null || _a === void 0 ? void 0 : _a.classList.add('footer-space-bottom');
    });
    const shippingAndReturnsType = useMemo(() => {
        if (current) {
            if (current === null || current === void 0 ? void 0 : current.isMbm) {
                return 'mbm';
            }
            else if (current === null || current === void 0 ? void 0 : current.isBoutiqueExclusive) {
                return 'boutique_exclusive';
            }
            else if ((current === null || current === void 0 ? void 0 : current.returnDisabled) && !(current === null || current === void 0 ? void 0 : current.isParfum)) {
                return 'not_returnable';
            }
            else if (current === null || current === void 0 ? void 0 : current.isParfum) {
                return 'parfum';
            }
            else {
                return 'standard';
            }
        }
        // OE-14837 Priority for content assets display
    }, [current]);
    useEffect(() => {
        //if (!shippingAndReturns && shippingAndReturnsType) { removed shippingAndReturnsType because too late rendering OE-21163 }
        if (!shippingAndReturns && shippingAndReturnsType) {
            api.getShippingAndReturns(shippingAndReturnsType).then((data) => {
                if (!(data === null || data === void 0 ? void 0 : data.errorMessage) && (data === null || data === void 0 ? void 0 : data.tabs)) {
                    setShippingAndReturns(data);
                    setShippingAndReturnsCheck(true);
                }
            });
        }
    }, [shippingAndReturnsType, current]);
    const { promoSection, BottomSection } = useProductEditorialContents({
        pid: 'pdp-'.concat((current === null || current === void 0 ? void 0 : current.id) || pid),
        setDetailsOpen,
        product: current,
        pdContent: ((_e = current === null || current === void 0 ? void 0 : current.editorial) === null || _e === void 0 ? void 0 : _e.pageDesignerContent) ? current.editorial.pageDesignerContent : null,
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Metatags, { pageMetaTags: current === null || current === void 0 ? void 0 : current.pageMetaTags, meta: current === null || current === void 0 ? void 0 : current.meta }),
        productSchemaData && React.createElement(JsonLd, { data: productSchemaData }),
        breadcrumbSchemaData && React.createElement(JsonLd, { data: breadcrumbSchemaData }),
        isMounted && (React.createElement("div", { className: classNames('product-container', 'product-container--hero', { 'white-bg': (current === null || current === void 0 ? void 0 : current.imageBackground) === 'white' }) },
            React.createElement("div", { className: "a11y-action-bar" },
                React.createElement("a", { href: "#product-info-heading" }, t('pdp.skiptoinfo')),
                ((current === null || current === void 0 ? void 0 : current.id) || (current === null || current === void 0 ? void 0 : current.hasLook) || (current === null || current === void 0 ? void 0 : current.breadcrumbs)) && React.createElement("a", { href: "#product-related-content" }, t('pdp.skiptorelated'))),
            React.createElement("div", { className: "product-col product-col--gallery" },
                React.createElement(ProductImageAnimated, { direction: "left", imageLoaded: heroImageLoaded },
                    React.createElement(React.Fragment, null,
                        ((_g = (_f = current === null || current === void 0 ? void 0 : current.mbm) === null || _f === void 0 ? void 0 : _f.palettes) === null || _g === void 0 ? void 0 : _g.length) > 0 && React.createElement("span", { className: "palette-available" }, t('mbm.paletteavailable', { 0: current.mbm.palettes.length })),
                        React.createElement(ProductHero, { clicked: galleryIsOpen, onImageClick: setGalleryIsOpen, product: current, media: media, openVto: () => setOpenVTO(true) })))),
            React.createElement("div", { className: "product-col product-col--selection" },
                React.createElement("div", { className: "product-selection-wrapper" },
                    React.createElement(ProductSelection, { size: props.size, contactformurl: props === null || props === void 0 ? void 0 : props.contactformurl, imageLoaded: heroImageLoaded, isScrolled: isScrolled, sizeguideOpen: sizeguideOpen, setSizeguideOpen: (open) => setSizeguideOpen(open), mbmBaa: props === null || props === void 0 ? void 0 : props.mbmBaa, openVto: () => setOpenVTO(true), setPlayButtonVideo: () => setPlayButtonVideo(), inPDP: true, setDetailsOpen: setDetailsOpen, setShippingOpen: setShippingOpen, shippingAndReturns: shippingAndReturnsCheck, openBalance: () => setCheckBalanceOpen(true), checkouturl: props.checkouturl })),
                React.createElement(ProductDiscoverMore, { loaded: heroImageLoaded })))),
        React.createElement(ScrollHandler, { current: current, loaded: heroImageLoaded }),
        React.createElement(BottomSection, null),
        React.createElement("div", { className: "product-container", id: "product-related-content" },
            (current === null || current === void 0 ? void 0 : current.hasLook) && React.createElement(ProductLook, { look: current === null || current === void 0 ? void 0 : current.look, product: current }),
            (current === null || current === void 0 ? void 0 : current.id) && React.createElement(ProductRecommendation, { customClasses: "pdp-recommendation", product: current, slot: "einstein-product-recommendations" }),
            (current === null || current === void 0 ? void 0 : current.id) && React.createElement(ProductRecommendation, { customClasses: "pdp-recommendation", product: current, slot: "einstein-product-recommendations-2" }),
            (current === null || current === void 0 ? void 0 : current.breadcrumbs) && (React.createElement("div", { className: "container" },
                React.createElement(Breadcrumbs, { product: current })))),
        current && (React.createElement(Drawers, { className: "details-modal", role: "dialog", isOpen: detailsOpen, onRequestClose: () => setDetailsOpen(false), contentLabel: t('pdp.detailsandcare.modal.title') },
            React.createElement(ProductDetailsTab, { contactformurl: props === null || props === void 0 ? void 0 : props.contactformurl, product: current, imageLoaded: heroImageLoaded, sizeguideOpen: sizeguideOpen, setSizeguideOpen: (open) => setSizeguideOpen(open), aftercareurl: (props === null || props === void 0 ? void 0 : props.aftercareurl) + 'product', promobanner: promoSection }))),
        shippingAndReturns && (React.createElement(Drawers, { className: "details-modal", role: "dialog", isOpen: shippingOpen, onRequestClose: () => setShippingOpen(false) },
            React.createElement(ProductShippingAndReturnsTabs, { product: current, shippingAndReturns: shippingAndReturns }))),
        React.createElement(ProductGallery, { isOpen: galleryIsOpen, scrollHeight: window.scrollY, onRequestClose: () => setGalleryIsOpen(false), product: current, media: media }),
        props.vtoBin && (React.createElement(VtoModal, { product: current, open: openVTO, modalImage: (_h = current === null || current === void 0 ? void 0 : current.imgs) === null || _h === void 0 ? void 0 : _h.urls[0], onClose: () => setOpenVTO(false), basePath: props.basePath, vtoBin: props.vtoBin })),
        React.createElement(GiftCardBalanceModal, { isOpen: checkBalanceOpen, pid: pid, onRequestClose: () => setCheckBalanceOpen(false) })));
};
export default ProductPage;
